
import { Component, Ref, Vue, namespace } from 'nuxt-property-decorator';
import { omit } from 'lodash-es';
import {
  EventDto,
  PageScope,
  FormEntryDto,
  FormDto,
  getFormEntryValue,
  PageType,
  FormType,
  PaginatedList,
  SchedulePresentationForFormEntryDto,
  RoomDto,
  PageDto,
  PresentationMedia,
  isSuccess,
  SubmissionScope,
  SubmissionDecisionEnum,
} from 'fourwaves-shared';
import { PresentationDetails, ContactPresenterModal, MediaViewer } from 'fourwaves-shared/components';
import { NuxtError } from '@nuxt/types';

const EventsModule = namespace('events');

@Component({
  scrollToTop: true,
  components: {
    PresentationDetails,
    MediaViewer,
    ContactPresenterModal,
  },
  validate({ params, store }) {
    // Id validation
    if (!/^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/.test(params.id)) {
      return false;
    }
    const page = store.state.events.pages.find(page => page.type === PageType.Abstracts);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isAllowedToVirtualCall']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // when the user come from the presentations page we want to keep filters
      if (from?.name && (from.name.startsWith('slug-abstracts__') || from.name.startsWith('slug-participants-id__'))) {
        (vm as AbstractPage).useBrowserHistory = true;
      }
    });
  },
})
export default class AbstractPage extends Vue {
  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.Getter isAllowedToVirtualCall!: boolean;
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.Getter isLiveAppOpen!: boolean;
  @EventsModule.State pages!: PageDto[];

  @Ref() contactPresenterModal?: ContactPresenterModal;

  form: FormDto | null = null;
  presentation: FormEntryDto | null = null;
  useBrowserHistory = false;
  pageType: PageType = PageType.Abstracts;
  allSchedulePresentations: PaginatedList<SchedulePresentationForFormEntryDto> | null = null;
  allRooms: RoomDto[] | null = null;
  lastUpdateTimestamp = 0;
  focusMessageId = '';

  get presentationTitle() {
    return this.presentation && this.form ? this.getSubmissionTitle(this.presentation, this.form) : '';
  }

  get presentationMedia(): PresentationMedia {
    return {
      poster: this.posterField,
      video: this.videoField,
      slides: this.slidesField,
      figures: this.figuresField,
    };
  }

  get posterField() {
    if (!this.form || !this.presentation) return null;

    const field = this.form.formSections
      .flatMap(({ formFields }) => formFields)
      .find(({ fieldType }) => fieldType === 'poster');

    if (!field) return null;
    const poster = getFormEntryValue(this.presentation, this.form, field.id);
    return !poster ? null : { field, value: `${this.$api.getFileUploadUrl(poster)}?auto=format` };
  }

  get videoField() {
    if (!this.form || !this.presentation) return null;

    const field = this.form.formSections
      .flatMap(({ formFields }) => formFields)
      .find(({ fieldType }) => fieldType === 'video');

    if (!field) return null;
    const video = getFormEntryValue(this.presentation, this.form, field.id);
    return !video ? null : { field, value: this.$api.getFileUploadUrl(video) };
  }

  get slidesField() {
    if (!this.form || !this.presentation) return null;

    const field = this.form.formSections
      .flatMap(({ formFields }) => formFields)
      .find(({ fieldType }) => fieldType === 'slides');

    if (!field) return null;
    const slidesFileId = getFormEntryValue(this.presentation, this.form, field.id) || null;

    return !slidesFileId ? null : { field, value: slidesFileId };
  }

  get figuresField() {
    if (!this.form) return null;

    const field = this.form.formSections
      .flatMap(({ formFields }) => formFields)
      .find(({ fieldType }) => fieldType === 'images');

    if (!this.presentation || !field) return null;

    const value = this.presentation.formEntryValues
      .filter(({ formFieldId }) => formFieldId === field!.id)
      .map(({ value }) => {
        const { id, title, description } = JSON.parse(value);
        return { id, title, description, url: `${this.$api.getFileUploadUrl(id)}?w=2400&fit=max&auto=format` };
      });

    return !value.length ? null : { field, value };
  }

  get arePresentationsVisible() {
    const schedulePage = this.pages.find(page => page.type === PageType.Schedule);
    if (!schedulePage || !schedulePage.isVisible) return false;
    return schedulePage.scope === PageScope.Everyone || this.isParticipant || this.isOrganizer;
  }

  get submissionScope(): SubmissionScope | null {
    const presentationsPage = this.pages.find(page => page.type === PageType.Abstracts)!;
    return presentationsPage ? presentationsPage.submissionScope : null;
  }

  async created() {
    this.$wait.start('loading');
    this.form = this.currentEvent.forms.find(({ type }) => type === FormType.Submission)!;
    this.presentation = await this.$api.getFormEntry(this.$route.params.id);

    if (!this.presentation)
      return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '404' } }));

    if (
      this.submissionScope === SubmissionScope.Accepted &&
      !this.presentation.submissionDecisions.some(({ decision }) => decision === SubmissionDecisionEnum.Accepted)
    ) {
      return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '404' } }));
    }

    const participantPageVisible: boolean = !!this.pages.find(({ type }) => type === PageType.Participants)?.isVisible;

    if (!participantPageVisible) {
      this.presentation.authors?.forEach(author => {
        author.participantId = null;
      });
    }

    const schedulePresentationsPromise = this.pages.find(({ type }) => type === PageType.Schedule)?.isVisible
      ? this.$api.getSessionPresentationsByFormEntryId({
          formEntryId: this.$route.params.id,
          page: 1,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
      : Promise.resolve({
          items: [] as SchedulePresentationForFormEntryDto[],
          totalPages: 0,
          totalCount: 0,
          count: 0,
          pageSize: 0,
        } as PaginatedList<SchedulePresentationForFormEntryDto>);

    const [schedulePresentations, rooms] = await Promise.all([
      schedulePresentationsPromise,
      this.$api.getRooms(this.currentEvent.id),
    ]);

    this.allSchedulePresentations = schedulePresentations;
    this.allRooms = rooms;
    this.$wait.end('loading');

    if (this.$route.query?.messageId) {
      this.focusMessageId = String(this.$route.query.messageId);
      this.$router.replace({ query: omit(this.$route.query, ['messageId']) });
    }

    if (this.$route.query?.scrollToComments) {
      await this.$router.replace({ query: omit(this.$route.query, ['scrollToComments']) });
      this.scrollToComments();
    }
  }

  public head() {
    return { title: this.presentation && this.form ? this.getSubmissionTitle(this.presentation, this.form) : '' };
  }

  public async toggleUpvote() {
    if (!this.presentation) return;
    if (!(this.isParticipant || this.isOrganizer)) return;

    if (this.presentation.isUpvotedByCurrentUser) {
      const result = await this.$api.deleteFormEntryUpvote(this.presentation.id);
      if (isSuccess(result)) {
        this.presentation.upvoteCount = this.presentation.upvoteCount - 1;
        this.presentation.isUpvotedByCurrentUser = false;
      }
    } else {
      const result = await this.$api.upvoteFormEntry(this.presentation.id);
      if (isSuccess(result)) {
        this.presentation.upvoteCount = this.presentation.upvoteCount + 1;
        this.presentation.isUpvotedByCurrentUser = true;
      }
    }
  }

  public async toggleBookmark() {
    if (!this.presentation) return;
    if (!(this.isParticipant || this.isOrganizer)) return;

    if (this.presentation.isBookmarkedByCurrentUser) {
      const result = await this.$api.unbookmarkFormEntry(this.presentation.id);
      if (isSuccess(result)) {
        this.presentation.isBookmarkedByCurrentUser = false;
      }
    } else {
      const result = await this.$api.bookmarkFormEntry(this.presentation.id);
      if (isSuccess(result)) {
        this.presentation.isBookmarkedByCurrentUser = true;
      }
    }
  }

  public scrollToComments() {
    this.$scrollTo('[data-scroll="qna"]', { offset: -100 });
  }

  public handleMessageCountUpdate(count: number) {
    if (!this.presentation) return;
    this.presentation.commentCount = count;
  }

  public openContactPresenterModal(event: PointerEvent) {
    this.contactPresenterModal?.triggerVisibility(event.currentTarget);
  }
}
